import { useState } from 'react';
import type { Wallet as WalletType } from '../shared/api';
import { MAIL_RATE_CENTS, MIN_TOPUP_CENTS } from '../shared/api';
import { Banner, Button, Card, DataTable, Field, Input, Modal, Pill, Spinner, Td } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { centsToDollars, fmtDateTime } from '../lib/format';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';

const PRESETS = [2500, 5000, 10000];

const TXN_LABELS: Record<string, string> = {
  topup: 'Top-up',
  send_debit: 'Certified mail',
  refund: 'Refund',
  adjustment: 'Adjustment',
};

/**
 * The certified mail wallet. Mail is a hard-gated spend: no balance, no send,
 * and a signed acknowledgment that we mail on the contractor's behalf.
 */
export default function Wallet() {
  const { contractor, refresh } = useSession();
  const { data, loading, error, reload } = useAsync(() => api.get<WalletType>('/api/billing/wallet'), []);
  const [topup, setTopup] = useState<number | null>(null);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const acknowledge = async () => {
    setBusy(true);
    setActionError(null);
    try {
      await api.post('/api/billing/wallet/ack');
      await Promise.all([refresh(), reload()]);
      setNotice('Certified mail acknowledgment signed.');
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not record the acknowledgment');
    } finally {
      setBusy(false);
    }
  };

  const startTopup = async (cents: number) => {
    setBusy(true);
    setActionError(null);
    try {
      const intent = await api.post<{ client_secret: string; payment_intent_id: string }>('/api/billing/wallet/topup', {
        amount_cents: cents,
      });
      // Stripe confirmation runs in Stripe's own flow; the balance moves when
      // the payment_intent.succeeded webhook lands, not when this returns.
      setTopup(null);
      setNotice(
        `Top-up of ${centsToDollars(cents)} started (${intent.payment_intent_id}). Your balance updates as soon as Stripe confirms the payment.`,
      );
      await Promise.all([refresh(), reload()]);
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Could not start the top-up');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Spinner label="Loading your wallet…" />;
  if (error) return <Banner tone="urgent" title="Could not load the wallet">{error}</Banner>;

  const wallet = data;
  const balance = wallet?.balance_cents ?? contractor?.mailBalanceCents ?? 0;
  const acknowledged = !!(wallet?.mail_ack_at ?? contractor?.mailAckAt);
  const lettersLeft = Math.floor(balance / MAIL_RATE_CENTS);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-ink">Certified mail wallet</h1>
        <p className="text-sm text-muted">Notices to Owner and demand letters draw from this balance.</p>
      </div>

      {notice && <Banner tone="ok">{notice}</Banner>}
      {actionError && <Banner tone="urgent">{actionError}</Banner>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-dim">Current balance</p>
          <p className={`mt-2 text-5xl font-bold tabular-nums ${balance >= MIN_TOPUP_CENTS ? 'text-ok' : 'text-urgent'}`}>
            {centsToDollars(balance)}
          </p>
          <p className="mt-2 text-sm text-muted">
            {centsToDollars(MAIL_RATE_CENTS)} per certified letter — about {lettersLeft} letter
            {lettersLeft === 1 ? '' : 's'} of runway.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {PRESETS.map((cents) => (
              <Button key={cents} variant="secondary" onClick={() => void startTopup(cents)} disabled={busy}>
                Add {centsToDollars(cents)}
              </Button>
            ))}
            <Button onClick={() => setTopup(0)} disabled={busy}>
              Add funds…
            </Button>
          </div>
        </Card>

        <Card>
          <p className="text-xs font-semibold uppercase tracking-wider text-dim">Certified mail acknowledgment</p>
          <div className="mt-3">
            {acknowledged ? (
              <Pill tone="ok">Signed {fmtDateTime(wallet?.mail_ack_at ?? contractor?.mailAckAt)}</Pill>
            ) : (
              <Pill tone="urgent">Not signed</Pill>
            )}
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            You authorise LienGuardian to send certified mail on your behalf and confirm the recipient addresses you supply
            are accurate. Nothing mails until this is signed.
          </p>
          <Button variant={acknowledged ? 'secondary' : 'primary'} onClick={acknowledge} disabled={busy} className="mt-4 w-full">
            {acknowledged ? 'Re-sign acknowledgment' : 'Sign acknowledgment'}
          </Button>
        </Card>
      </div>

      <Card title="Transaction history">
        <DataTable head={['Date', 'Type', 'Description', 'Amount', 'Balance after']} empty="No transactions yet.">
          {(wallet?.transactions ?? []).slice(0, 50).map((t) => (
            <tr key={t.id} className="border-b border-border/60">
              <Td className="whitespace-nowrap text-muted">{fmtDateTime(t.createdAt)}</Td>
              <Td>
                <Pill tone={t.amountCents >= 0 ? 'ok' : 'none'}>{TXN_LABELS[t.type] ?? t.type}</Pill>
              </Td>
              <Td className="text-muted">{t.description ?? '—'}</Td>
              <Td className={`tabular-nums font-semibold ${t.amountCents >= 0 ? 'text-ok' : 'text-ink'}`}>
                {t.amountCents >= 0 ? '+' : '−'}
                {centsToDollars(Math.abs(t.amountCents))}
              </Td>
              <Td className="tabular-nums text-muted">{centsToDollars(t.balanceAfterCents)}</Td>
            </tr>
          ))}
        </DataTable>
      </Card>

      {topup !== null && (
        <Modal title="Add funds" onClose={() => setTopup(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void startTopup(Math.round(Number(custom) * 100));
            }}
            className="space-y-4"
          >
            <Field label="Amount (USD)" hint={`Minimum ${centsToDollars(MIN_TOPUP_CENTS)}.`}>
              <Input
                required
                type="number"
                min={MIN_TOPUP_CENTS / 100}
                step="1"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="50"
              />
            </Field>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setTopup(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy || Math.round(Number(custom) * 100) < MIN_TOPUP_CENTS}>
                Continue to payment
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
